import { CIQUAL_SOURCE_NOTE, findCiqualFoodByName } from '../data/ciqualEssentialFoods';

const MEDICAL_WARNING = 'Avertissement medical : Le contenu de cette application est fourni uniquement a titre informatif et educatif. Aucune information presente ici ne remplace un avis medical, un diagnostic ou une recommandation d un professionnel de sante. En cas de doute, de pathologie, d allergie, de traitement en cours ou de regime specifique, consulter un professionnel de sante qualifie.';

const SHEETS = {
  beurre: {
    nom: 'Beurre',
    description: 'Matiere grasse laitiere utilisee en tartine, cuisson et patisserie.',
    utiliteNutritionnelle: 'Apporte des lipides et des vitamines liposolubles (A, D selon enrichissement).',
    origine: 'Produit issu du barattage de la creme de lait.',
    interetsSante: 'Peut etre utile en petite quantite pour l apport energetique.',
    risquesPrecautions: 'A consommer avec moderation en cas de cholesterol eleve ou regime hypocalorique.',
    conservation: 'Conserver au refrigerateur, bien protege des odeurs.',
    dureeConservation: 'En general 2 a 4 semaines apres ouverture selon marque.',
    choixProduit: 'Verifier la liste d ingredients et privilegier les versions simples.',
    usages: 'Cuisson douce, tartines, sauces, patisserie.',
    recettesRapides: ['Pates beurre-citron', 'Puree maison', 'Tartine beurre-miel'],
    remarques: 'Le beurre de vaches nourries a l herbe peut presenter un profil lipidique different.',
    aSavoir: 'A consommer avec moderation.',
    nutrition100g: {
      calories: 717,
      proteines: 0.9,
      glucides: 0.1,
      sucres: 0.1,
      fibres: 0,
      lipides: 81,
      graisses_saturees: 51,
      sodium: 0.7,
      indice_glycemique: null,
      omega3: 0.8,
    },
  },
};

const FALLBACK_SHEET = {
  description: 'Aliment du quotidien a integrer selon vos besoins nutritionnels et votre contexte de sante.',
  utiliteNutritionnelle: 'Contribue a l apport nutritionnel global selon quantite et frequence.',
  origine: 'Variable selon le produit et la filiere.',
  interetsSante: 'Peut trouver sa place dans une alimentation equilibree.',
  risquesPrecautions: 'Verifier les portions, sucres, sel, graisses et allergenes.',
  conservation: 'Respecter la chaine du froid et les indications de l emballage.',
  dureeConservation: 'Se referer a la DLC/DDM du produit.',
  choixProduit: 'Preferer une liste d ingredients courte et des produits peu transformes.',
  usages: 'Adapter les usages selon vos objectifs et habitudes.',
  recettesRapides: ['Salade composee', 'Poelee rapide', 'Bol petit-dejeuner'],
  remarques: 'Les besoins nutritionnels varient selon la personne.',
  aSavoir: 'Verifier les ingredients en cas d allergie.',
  nutrition100g: {
    calories: null,
    proteines: null,
    glucides: null,
    sucres: null,
    fibres: null,
    lipides: null,
    graisses_saturees: null,
    sodium: null,
    indice_glycemique: null,
    omega3: null,
  },
};

function normalizeName(name) {
  return String(name || '').trim().toLowerCase();
}

function pickFirstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
}

function toNutritionMap(raw = {}) {
  const cleaned = {};
  Object.entries(raw || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
}

export function getFoodEducationSheet(foodName, nutritionReference = {}) {
  const key = normalizeName(foodName);
  const exact = SHEETS[key] || null;
  const ciqualFood = findCiqualFoodByName(foodName);

  // Extract educational fields from CIQUAL object (built by buildEducation in ciqualEssentialFoods.js)
  const ciqualEducation = ciqualFood ? {
    description: ciqualFood.description,
    utiliteNutritionnelle: ciqualFood.utiliteNutritionnelle,
    origine: ciqualFood.origine,
    interetsSante: ciqualFood.interetsSante,
    risquesPrecautions: ciqualFood.risquesPrecautions,
    conservation: ciqualFood.conservation,
    dureeConservation: ciqualFood.dureeConservation,
    choixProduit: ciqualFood.choixProduit,
    usages: ciqualFood.usages,
    recettesRapides: ciqualFood.recettesRapides,
    remarques: ciqualFood.remarques,
    aSavoir: ciqualFood.aSavoir,
  } : {};

  // Priority: exact hand-written sheet > CIQUAL-generated content > generic fallback
  const merged = {
    ...FALLBACK_SHEET,
    ...ciqualEducation,
    ...(exact || {}),
  };

  return {
    nom: exact?.nom || ciqualFood?.nom || foodName || 'Aliment',
    ...merged,
    nutrition100g: {
      ...FALLBACK_SHEET.nutrition100g,
      ...(ciqualFood?.nutrition100g || {}),
      ...toNutritionMap(nutritionReference || {}),
      ...(exact?.nutrition100g || {}),
    },
    sourceNote: CIQUAL_SOURCE_NOTE,
    medicalWarning: MEDICAL_WARNING,
  };
}

export function inferNutritionReference(product = {}) {
  const nutrition = product.nutrition || {};
  return {
    calories: pickFirstDefined(
      nutrition.calories_100g,
      nutrition.energy_kcal_100g,
      nutrition.energy_100g,
      product.energy_kcal_100g,
      product.calories_100g,
    ),
    proteines: pickFirstDefined(
      nutrition.proteins_100g,
      nutrition.proteines_100g,
      product.proteins_100g,
      product.proteines_100g,
    ),
    glucides: pickFirstDefined(
      nutrition.carbs_100g,
      nutrition.carbohydrates_100g,
      nutrition.glucides_100g,
      product.carbohydrates_100g,
      product.carbs_100g,
      product.glucides_100g,
    ),
    sucres: pickFirstDefined(
      nutrition.sugars_100g,
      nutrition.sucres_100g,
      product.sugars_100g,
      product.sucres_100g,
    ),
    fibres: pickFirstDefined(
      nutrition.fiber_100g,
      nutrition.fibres_100g,
      product.fiber_100g,
      product.fibres_100g,
    ),
    lipides: pickFirstDefined(
      nutrition.fat_100g,
      nutrition.lipides_100g,
      product.fat_100g,
      product.lipides_100g,
    ),
    graisses_saturees: pickFirstDefined(
      nutrition.saturated_fat_100g,
      nutrition.graisses_saturees_100g,
      product.saturated_fat_100g,
      product.graisses_saturees_100g,
    ),
    sodium: pickFirstDefined(
      nutrition.sodium_100g,
      nutrition.salt_100g,
      product.sodium_100g,
      product.salt_100g,
    ),
    indice_glycemique: pickFirstDefined(
      nutrition.glycemic_index,
      nutrition.indice_glycemique,
      product.glycemic_index,
      product.indice_glycemique,
    ),
    omega3: pickFirstDefined(
      nutrition.omega3_100g,
      product.omega3_100g,
    ),
  };
}
